/**
 * AuthLayout — shared wrapper for all auth pages.
 *
 * From the Claude Design "JAI Onboarding" handoff (auth-shell.jsx / AuthStage):
 * - Wide screens (≥900px): split layout — navy brand hero on the left
 *   (mood-aware mascot + wordmark, "Stop guessing / Start winning" headline,
 *   three selling points, "trusted by" footer), white form column on the right.
 * - Narrow screens: centered glassy card with the mascot above it.
 * - A "Back to site" pill (top-left) returns to the marketing landing.
 *
 * Usage: <AuthLayout screen="login"> ...form... </AuthLayout>
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { JaiMark, JaiWordmark, JAI_COLORS, JAI_FONTS } from './JaiBrand'
import JAIMascot from './JAIMascot'
import { CheckIcon, ArrowLeft } from './AuthFields'
import { IS_NATIVE_APP } from '../services/runtime'

// Which mascot mood goes with which screen
const MASCOT_MOODS = { login: 'idle', signup: 'excited', forgot: 'thinking', reset: 'focused' }

// Marketing points for the split brand panel (from the new onboarding design)
const HERO_POINTS = [
  ['Live draft help tuned to your roster', 'Your scoring, your keepers, your exact pick.'],
  ['Trades & waivers graded in seconds', 'Ask JAI — get the math and the verdict.'],
  ['Just ask JAI', 'One place to ask — it reaches for whichever of the nine tools fits.'],
]

// Fine grain texture used over the dark brand panel
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)' opacity='0.7'/%3E%3C/svg%3E\")"

function CheckDot() {
  return (
    <span style={{ width: 21, height: 21, borderRadius: 7, flexShrink: 0, marginTop: 1, border: '1.5px solid rgba(58,124,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CheckIcon size={12} stroke={JAI_COLORS.blueBright} sw={2.6} />
    </span>
  )
}

// ── "Back to site" pill → marketing landing (web only) ───────
function BackToSite({ dark }) {
  const navigate = useNavigate()
  const c = dark ? 'rgba(255,255,255,0.7)' : JAI_COLORS.mute
  return (
    <button type="button" onClick={() => navigate('/welcome')}
      style={{ position: 'absolute', top: 20, left: 22, zIndex: 5, display: 'inline-flex', alignItems: 'center', gap: 7, background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(10,31,68,0.05)', border: `1px solid ${dark ? 'rgba(255,255,255,0.16)' : JAI_COLORS.hair}`, borderRadius: 999, padding: '7px 14px 7px 11px', cursor: 'pointer', color: c, fontSize: 12.5, fontWeight: 600, fontFamily: JAI_FONTS.tight, backdropFilter: 'blur(8px)' }}
      onMouseEnter={(e) => { e.currentTarget.style.color = dark ? 'white' : JAI_COLORS.navy }}
      onMouseLeave={(e) => { e.currentTarget.style.color = c }}>
      <ArrowLeft size={14} /> Back to site
    </button>
  )
}

// ── Plain back button (native) ───────────────────────────────
// "Back to site" makes no sense inside an app — there's no site to return
// to, just the welcome screen underneath. A bare arrow reads as ordinary
// in-app back navigation instead of a leftover web affordance.
function NativeBack() {
  const navigate = useNavigate()
  return (
    <button type="button" onClick={() => navigate('/welcome')} aria-label="Back"
      style={{
        position: 'absolute', top: 'calc(var(--safe-top) + 16px)', left: 16, zIndex: 5,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 40, height: 40, background: 'none', border: 'none',
        color: JAI_COLORS.mute, cursor: 'pointer',
      }}>
      <ArrowLeft size={20} />
    </button>
  )
}

// ── Brand hero panel (left side of split layout) ────────────
function HeroPanel({ screen }) {
  return (
    <div style={{
      position: 'relative', overflow: 'hidden', flex: '0 0 46%', minWidth: 0,
      background: `linear-gradient(155deg, ${JAI_COLORS.navy} 0%, #061633 100%)`,
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '40px 44px 38px',
    }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 82% 10%, rgba(58,124,255,0.32), transparent 54%)' }} />
      <div style={{ position: 'absolute', inset: 0, opacity: 0.05, pointerEvents: 'none', backgroundImage: GRAIN }} />
      <div style={{ position: 'absolute', right: -80, bottom: -80, opacity: 0.1, pointerEvents: 'none' }}>
        <JaiMark size={360} accent={JAI_COLORS.blueBright} />
      </div>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12 }}>
        <JAIMascot size={44} state={MASCOT_MOODS[screen] || 'idle'} />
        <JaiWordmark size={24} color="white" />
      </div>
      <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingBottom: 40, paddingTop: 20 }}>
        <div style={{ fontFamily: JAI_FONTS.mono, fontSize: 10.5, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 18 }}>
          Your AI co-manager · always on
        </div>
        <h1 style={{ fontFamily: JAI_FONTS.tight, fontSize: 'clamp(34px, 4vw, 46px)', lineHeight: 0.96, fontWeight: 880, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.05em', color: 'white', margin: 0 }}>
          Stop guessing.<br /><span style={{ color: JAI_COLORS.blueBright }}>Start winning.</span>
        </h1>
        <div style={{ marginTop: 30, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {HERO_POINTS.map(([h, s]) => (
            <div key={h} style={{ display: 'flex', gap: 12 }}>
              <CheckDot />
              <div>
                <div style={{ fontFamily: JAI_FONTS.tight, fontSize: 14, fontWeight: 650, color: 'white', lineHeight: 1.3 }}>{h}</div>
                <div style={{ fontFamily: JAI_FONTS.tight, fontSize: 12.5, color: 'rgba(255,255,255,0.55)', marginTop: 2, lineHeight: 1.4 }}>{s}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 9, fontFamily: JAI_FONTS.tight, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
        <div style={{ display: 'flex' }}>
          {['#3A7CFF', '#1A8754', '#C7841C'].map((c, i) => (
            <div key={i} style={{ width: 22, height: 22, borderRadius: '50%', background: c, border: '2px solid #0A1F44', marginLeft: i ? -7 : 0 }} />
          ))}
        </div>
        Trusted by managers in 1,200+ leagues
      </div>
    </div>
  )
}

// ── Layout root ─────────────────────────────────────────────
export default function AuthLayout({ screen, children }) {
  const [vw, setVw] = useState(window.innerWidth)

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const useSplit = vw >= 900

  if (useSplit) {
    return (
      <div style={{ minHeight: 'calc(100vh / var(--jai-zoom))', display: 'flex', background: JAI_COLORS.white, fontFamily: JAI_FONTS.tight, position: 'relative' }}>
        <HeroPanel screen={screen} />
        <BackToSite dark />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 32px', background: JAI_COLORS.white }}>
          <div style={{ width: '100%', maxWidth: 384, margin: '0 auto' }}>
            {children}
          </div>
        </div>
      </div>
    )
  }

  // Native gets one flat page — no card floating on a differently-shaded
  // background (requested directly: that read as a popup, not a screen).
  // The outer and "card" backgrounds are the same colour and the border/
  // shadow/blur that used to separate them are dropped. Web's narrow-viewport
  // look (a glassy card on the paper background) is untouched.
  return (
    <div style={{
      minHeight: 'calc(100vh / var(--jai-zoom))', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: IS_NATIVE_APP ? '16px' : '78px 16px 44px',
      paddingTop: IS_NATIVE_APP ? 'calc(var(--safe-top) + 64px)' : undefined,
      paddingBottom: IS_NATIVE_APP ? 'calc(var(--safe-bottom) + 24px)' : undefined,
      background: JAI_COLORS.paper, fontFamily: JAI_FONTS.tight, position: 'relative',
    }}>
      {IS_NATIVE_APP ? <NativeBack /> : <BackToSite />}
      <div style={{ width: '100%', maxWidth: 432 }}>
        <div style={IS_NATIVE_APP ? {
          background: JAI_COLORS.paper, borderRadius: 0, padding: 0,
        } : {
          background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(10px)', borderRadius: 20,
          padding: '34px 32px', border: `1px solid ${JAI_COLORS.hair}`, boxShadow: '0 12px 48px rgba(10,31,68,0.10), 0 2px 8px rgba(10,31,68,0.04)',
        }}>
          <div style={{ width: '100%', maxWidth: 384, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <JAIMascot size={66} state={MASCOT_MOODS[screen] || 'idle'} />
                <JaiWordmark size={24} />
              </div>
            </div>
            {children}
          </div>
        </div>
        <p style={{ textAlign: 'center', fontSize: 11.5, color: AUTH_FAINT, marginTop: 20, fontFamily: JAI_FONTS.tight }}>
          © 2026 JAI · Smarter fantasy picks
        </p>
      </div>
    </div>
  )
}

const AUTH_FAINT = 'rgba(10,31,68,0.40)'
